import React, { useRef, useState } from "react";
import { BACKEND_URL } from "../lib/api";
import { Play, Pause, RotateCcw, RotateCw, Loader2 } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return (h ? `${h}:${String(m).padStart(2, "0")}` : `${m}`) + `:${String(sec).padStart(2, "0")}`;
};

export const AudioPlayer = ({ getUrl, testId = "audio-player" }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState("");
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [error, setError] = useState("");
  const retried = useRef(false);

  const loadSrc = async () => {
    const url = await getUrl();
    ref.current.src = `${BACKEND_URL}${url}`;
    setSrc(ref.current.src);
  };

  const onError = async () => {
    if (!src) return;
    if (retried.current) { setError("Playback failed"); setLoading(false); return; }
    retried.current = true;
    const a = ref.current, at = a.currentTime;
    try { await loadSrc(); a.currentTime = at; a.play().catch(() => {}); } catch { setError("Playback failed"); setLoading(false); }
  };

  const toggle = async () => {
    const a = ref.current;
    if (!src) {
      setLoading(true); setError("");
      try { await loadSrc(); } catch { setError("Could not load recording"); setLoading(false); return; }
    }
    if (a.paused) a.play().catch(() => { setError("Playback failed"); setLoading(false); });
    else a.pause();
  };

  const skip = (d) => { const a = ref.current; if (!src) return; a.currentTime = Math.max(0, Math.min((a.duration || 0), a.currentTime + d)); };
  const seek = (e) => { const a = ref.current; if (!src || !dur) return; a.currentTime = Number(e.target.value); setTime(a.currentTime); };
  const pct = dur ? (time / dur) * 100 : 0;

  return (
    <div className="rounded-md border bg-white p-3" style={{ borderColor: 'rgba(13,42,32,0.1)' }} data-testid={testId}>
      <audio ref={ref} preload="none"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onWaiting={() => setLoading(true)} onPlaying={() => { setLoading(false); retried.current = false; }} onCanPlay={() => setLoading(false)}
        onTimeUpdate={(e) => setTime(e.target.currentTime)} onLoadedMetadata={(e) => setDur(e.target.duration)}
        onDurationChange={(e) => setDur(e.target.duration)} onError={onError} />
      <div className="flex items-center gap-1 min-w-0">
        <button type="button" onClick={() => skip(-15)} disabled={!src} aria-label="Back 15 seconds" data-testid={`${testId}-back15`}
          className="shrink-0 h-9 px-1.5 rounded-full flex items-center gap-0.5 hover:bg-[#ece5d3] disabled:opacity-40 transition-colors">
          <RotateCcw size={16} /><span className="text-[11px] font-semibold tabular-nums">15</span>
        </button>
        <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`${testId}-toggle`}
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center btn-coral transition-transform hover:scale-105">
          {loading ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <button type="button" onClick={() => skip(15)} disabled={!src} aria-label="Forward 15 seconds" data-testid={`${testId}-fwd15`}
          className="shrink-0 h-9 px-1.5 rounded-full flex items-center gap-0.5 hover:bg-[#ece5d3] disabled:opacity-40 transition-colors">
          <span className="text-[11px] font-semibold tabular-nums">15</span><RotateCw size={16} />
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-1.5 ml-1">
          <span className="text-[11px] tabular-nums text-[#0d2a20]/70 shrink-0 text-right" data-testid={`${testId}-current`}>{fmt(time)}</span>
          <input type="range" min={0} max={dur || 0} step="0.1" value={time} onChange={seek} disabled={!dur}
            aria-label="Seek" data-testid={`${testId}-progress`} className="flex-1 min-w-0 w-full h-1.5 cursor-pointer accent-[#e0684d]"
            style={{ background: `linear-gradient(to right, #e0684d ${pct}%, #ece5d3 ${pct}%)`, borderRadius: 999, appearance: 'none' }} />
          <span className="text-[11px] tabular-nums text-[#0d2a20]/70 shrink-0" data-testid={`${testId}-duration`}>{fmt(dur)}</span>
        </div>
      </div>
      {error && <div className="text-[12px] text-[#7a1f13] mt-2" data-testid={`${testId}-error`}>{error}</div>}
    </div>
  );
};

export default AudioPlayer;
